import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Lightbulb, RefreshCw, Target, Brain, Zap } from 'lucide-react';
import { toast } from 'sonner';

function MeaningMaker({ onBack }) {
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState({ event: '', meaning: '' });
  const [insights, setInsights] = useState([]);
  const [level, setLevel] = useState(1);

  const scenarioPrompts = [
    { trigger: "Somebody said to me", example: "You're always late" },
    { trigger: "I heard somebody say to someone else", example: "They're so irresponsible" },
    { trigger: "I saw someone do", example: "Walk away while I was talking" },
    { trigger: "Someone looked at me", example: "With a confused expression" },
    { trigger: "I received a text that said", example: "We need to talk" },
    { trigger: "Someone didn't respond when", example: "I said hello in the hallway" },
    { trigger: "I noticed someone", example: "Checking their phone during our conversation" }
  ];

  const commonMeanings = [
    "They don't like me",
    "I did something wrong", 
    "They don't respect me",
    "I'm not important",
    "They're judging me",
    "I'm being rejected",
    "They think I'm stupid",
    "I don't matter",
    "They're angry at me",
    "I'm not good enough"
  ];

  const alternativeMeanings = [
    "They might be having a difficult day",
    "They could be distracted by something important",
    "This might not be about me at all",
    "They may be processing something heavy",
    "Their response reflects their inner state, not mine",
    "They might not have noticed or heard me",
    "This could be their way of protecting themselves",
    "They may be overwhelmed or stressed"
  ];

  const generateScenario = () => {
    const randomPrompt = scenarioPrompts[Math.floor(Math.random() * scenarioPrompts.length)];
    setCurrentScenario({ 
      event: randomPrompt.trigger, 
      meaning: '',
      example: randomPrompt.example 
    });
    
    // Play generation sound
    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA2NjY2NjY2NjY2NjY2NjY2NjY2');
    audio.volume = 0.4;
    audio.play().catch(() => {});
  };

  const saveScenario = () => {
    if (currentScenario.event && currentScenario.meaning) {
      const newScenario = {
        id: Date.now(),
        event: currentScenario.event,
        meaning: currentScenario.meaning,
        level: level,
        timestamp: new Date().toLocaleString()
      };
      
      setScenarios(prev => [...prev, newScenario]);
      setCurrentScenario({ event: '', meaning: '' });
      
      if (scenarios.length > 0 && scenarios.length % 3 === 0) {
        setLevel(prev => prev + 1);
        toast.success(`🎉 Level ${level + 1} unlocked! You're gaining awareness of your meaning-making!`);
      } else {
        toast.success('Scenario saved! You\'re becoming more conscious of your interpretations.');
      }
      
      // Play save sound
      const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA3NzM3NzM3NzM3NzM3NzM3Nzc3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    }
  };

  const addInsight = (insight) => {
    setInsights(prev => [...prev, {
      id: Date.now(),
      text: insight,
      timestamp: new Date().toLocaleString()
    }]);
    toast.success('Insight captured! 💡');
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="min-h-screen bg-blue-900 bg-opacity-85 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <Button onClick={onBack} variant="outline" className="border-white text-white hover:bg-white hover:text-black mb-6" style={{ borderRadius: 0 }}>
              ← Back to Games & Tools
            </Button>
            <h1 className="text-4xl font-bold text-white mb-4">💭 Meaning Maker</h1>
            <p className="text-xl text-blue-200">Catch your interpretations and choose your responses</p>
            
            <div className="flex justify-center gap-6 mt-6">
              <Badge className="bg-blue-500 text-white text-lg px-4 py-2">
                🎯 Level {level}
              </Badge>
              <Badge className="bg-green-500 text-white text-lg px-4 py-2">
                📝 {scenarios.length} Scenarios
              </Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Current Scenario Builder */}
            <Card className="border-4 border-blue-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-blue-800 flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  Create Your Scenario
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div>
                  <Button 
                    onClick={generateScenario}
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 mb-4"
                    style={{ borderRadius: 0 }}
                  >
                    🎲 Generate Scenario Prompt
                  </Button>
                  
                  {currentScenario.event && (
                    <div className="bg-blue-50 p-4 border-l-4 border-blue-500">
                      <p className="text-sm font-bold text-blue-800">Scenario Prompt:</p>
                      <p className="text-blue-700">"{currentScenario.event}..."</p>
                      <p className="text-xs text-blue-600 mt-2">Example: {currentScenario.example}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">What happened? (The facts)</label>
                    <Input
                      placeholder="e.g., 'You never call me back'"
                      value={currentScenario.event}
                      onChange={(e) => setCurrentScenario(prev => ({ ...prev, event: e.target.value }))}
                      className="border-blue-500"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold mb-2">...and I made it mean:</label>
                    <Textarea
                      placeholder="What story did you create? What did you make it mean about you, them, or the situation?"
                      value={currentScenario.meaning}
                      onChange={(e) => setCurrentScenario(prev => ({ ...prev, meaning: e.target.value }))}
                      className="border-blue-500 min-h-[100px]"
                      style={{ borderRadius: 0 }}
                    />
                  </div>

                  {/* Quick Meaning Buttons */}
                  <div>
                    <p className="text-sm font-bold mb-2">Common meanings (click to use):</p>
                    <div className="grid grid-cols-2 gap-2">
                      {commonMeanings.slice(0, 8).map((meaning) => (
                        <Button
                          key={meaning}
                          onClick={() => setCurrentScenario(prev => ({ ...prev, meaning }))}
                          variant="outline"
                          className="border-gray-400 text-xs py-1 text-left"
                          style={{ borderRadius: 0 }}
                        >
                          {meaning}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={saveScenario}
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                  style={{ borderRadius: 0 }}
                  disabled={!currentScenario.event || !currentScenario.meaning}
                >
                  💾 Save & Get Insight
                </Button>
              </CardContent>
            </Card>

            {/* Insights & Alternative Meanings */}
            <Card className="border-4 border-green-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-green-800 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  Alternative Perspectives
                </CardTitle>
                <p className="text-sm text-green-700">Remember: All meaning is subjective - you have choice!</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* Alternative Meanings Game */}
                  <div>
                    <h4 className="font-bold text-green-800 mb-3">🔄 What Else Could It Mean?</h4>
                    <div className="space-y-2">
                      {alternativeMeanings.slice(0, 6).map((meaning, i) => (
                        <Button
                          key={meaning}
                          onClick={() => {
                            addInsight(`Alternative meaning: ${meaning}`);
                            const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA4ODg4ODg4ODg4ODg4ODg4ODg4');
                            audio.volume = 0.3;
                            audio.play().catch(() => {});
                          }}
                          variant="outline"
                          className="w-full text-left border-green-400 hover:bg-green-50 text-sm py-2"
                          style={{ borderRadius: 0 }}
                        >
                          💡 {meaning}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Power Question */}
                  <Card className="border-2 border-yellow-500 bg-yellow-50" style={{ borderRadius: 0 }}>
                    <CardContent className="p-4">
                      <h4 className="font-bold text-yellow-800 mb-2">⚡ Power Question:</h4>
                      <p className="text-sm text-yellow-700 italic">
                        "Since all meaning is totally subjective, how can I choose a meaning that serves my well-being and relationships?"
                      </p>
                    </CardContent>
                  </Card>

                  {/* Your Insights */}
                  {insights.length > 0 && (
                    <div>
                      <h4 className="font-bold text-green-800 mb-3">🌟 Your Insights</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {insights.slice(-5).map((insight) => (
                          <div key={insight.id} className="p-3 bg-green-50 border-l-4 border-green-500">
                            <p className="text-sm text-gray-700">{insight.text}</p>
                            <p className="text-xs text-gray-500 mt-1">{insight.timestamp}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Scenarios */}
          {scenarios.length > 0 && (
            <Card className="mt-8 border-4 border-purple-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-purple-800">📖 Your Meaning-Making Journal</CardTitle>
                <p className="text-sm text-purple-700">Track your growing awareness</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {scenarios.slice(-5).reverse().map((scenario) => (
                    <div key={scenario.id} className="p-4 bg-purple-50 border-l-4 border-purple-500">
                      <div className="flex justify-between items-start mb-2">
                        <Badge className="bg-purple-500 text-white text-xs">Level {scenario.level}</Badge>
                        <span className="text-xs text-gray-500">{scenario.timestamp}</span>
                      </div>
                      <p className="text-sm text-gray-800"><strong>Event:</strong> {scenario.event}</p>
                      <p className="text-sm text-gray-600 mt-1"><strong>I made it mean:</strong> {scenario.meaning}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Wisdom Card */}
          <Card className="mt-8 border-4 border-yellow-500 bg-white" style={{ borderRadius: 0 }}>
            <CardContent className="p-6 text-center">
              <Zap className="w-8 h-8 text-yellow-600 mx-auto mb-4" />
              <p className="text-lg italic text-gray-800 mb-3">
                "Since all the meaning we make of things is totally subjective, it is important to notice what we are making things mean so we can be more at choice about how we would like to respond."
              </p>
              <p className="text-base text-gray-600">— Steve Torma</p>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

export default MeaningMaker;